import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, XCircle, Euro, Clock, QrCode, Shield, ChefHat, Sparkles,
  Package, Leaf, FlaskConical, Scale,
} from 'lucide-react';

const faqs = [
  {
    q: '¿Es difícil migrar de Haddock a PLEKO?',
    a: 'No, la migración es muy sencilla. Puedes importar tus ingredientes desde Excel en pocos minutos con nuestro asistente de importación. Tu equipo estará operativo en menos de un día. Además, nuestro soporte te acompaña durante todo el proceso.',
  },
  {
    q: '¿PLEKO es realmente más barato que Haddock?',
    a: 'Sí. El plan BASIC de PLEKO cuesta 49€/mes frente a los 85€/mes del plan Growth de Haddock. Y el plan PRO de PLEKO (79€/mes) incluye funcionalidades como control horario, carta digital con QR y gestión de alérgenos que en Haddock no están disponibles o requieren su plan Premium de 120€/mes.',
  },
  {
    q: '¿PLEKO tiene escandallo automático como Haddock?',
    a: 'Sí, y va más allá. PLEKO calcula escandallos automáticamente incluyendo mermas, peso por ración y coste por kg. Los actualiza en tiempo real cuando cambias el precio de un ingrediente. Además incluye un generador de recetas con IA y un optimizador de food cost que Haddock no ofrece.',
  },
  {
    q: '¿Puedo probar PLEKO gratis antes de decidir?',
    a: 'Por supuesto. PLEKO ofrece 14 días de prueba gratuita sin necesidad de tarjeta de crédito. Puedes probar todas las funcionalidades del plan PRO sin compromiso. Haddock solo ofrece 10 días de prueba.',
  },
  {
    q: '¿PLEKO cumple con la normativa de control horario RD-ley 8/2019?',
    a: 'Sí. PLEKO incluye un módulo de control horario completo que cumple con el RD-ley 8/2019 en los planes PRO y SCALE. Registra entradas, salidas, pausas y genera los informes necesarios para inspecciones laborales. Haddock no ofrece esta funcionalidad.',
  },
  {
    q: '¿PLEKO tiene integraciones con TPV?',
    a: 'Sí. PLEKO se integra con Square, Last.app y Epos Now. Puedes sincronizar ventas, ver KPIs de facturación y cruzar datos de coste con ingresos reales directamente desde tu dashboard.',
  },
  {
    q: '¿Qué es el módulo de almacén de PLEKO?',
    a: 'Es un sistema de control de stock con movimientos de entrada/salida, alertas de stock mínimo y conexión directa con el OCR: cuando escaneas una factura, puedes dar de alta el stock automáticamente. Haddock no ofrece gestión de inventario real.',
  },
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const comparisonRows: { feature: string; pleko: boolean | string; haddock: boolean | string; type: 'bool' | 'text'; highlight?: boolean }[] = [
  { feature: 'Precio desde', pleko: '49€/mes', haddock: '85€/mes', type: 'text', highlight: true },
  { feature: 'Prueba gratuita', pleko: '14 días', haddock: '10 días', type: 'text' },
  { feature: 'Escandallos automáticos', pleko: true, haddock: true, type: 'bool' },
  { feature: 'OCR / digitalización de facturas', pleko: true, haddock: true, type: 'bool' },
  { feature: 'Alertas de variación de precios', pleko: true, haddock: true, type: 'bool' },
  { feature: 'Pedidos a proveedores', pleko: true, haddock: true, type: 'bool' },
  { feature: 'Dashboard de control', pleko: true, haddock: true, type: 'bool' },
  { feature: 'Integraciones con TPV', pleko: 'Square, Last.app, Epos Now', haddock: true, type: 'text' },
  { feature: 'Multi-restaurante', pleko: true, haddock: true, type: 'bool' },
  { feature: 'Gestión de equipos y roles', pleko: true, haddock: true, type: 'bool' },
  { feature: 'Control horario (RD-ley 8/2019)', pleko: true, haddock: false, type: 'bool', highlight: true },
  { feature: 'Carta digital con QR', pleko: true, haddock: false, type: 'bool', highlight: true },
  { feature: 'Gestión de alérgenos en carta', pleko: true, haddock: false, type: 'bool', highlight: true },
  { feature: 'Generador de recetas con IA', pleko: true, haddock: false, type: 'bool', highlight: true },
  { feature: 'Optimizador de food cost con IA', pleko: true, haddock: false, type: 'bool', highlight: true },
  { feature: 'Almacén / Control de stock', pleko: true, haddock: false, type: 'bool', highlight: true },
  { feature: 'Despieces (piezas grandes)', pleko: true, haddock: false, type: 'bool', highlight: true },
  { feature: 'Calendario de productos de temporada', pleko: true, haddock: false, type: 'bool' },
  { feature: 'Laboratorio Chef (I+D)', pleko: true, haddock: false, type: 'bool' },
  { feature: 'Escalador de raciones (producción)', pleko: true, haddock: false, type: 'bool' },
  { feature: 'Peso por ración / Coste por kg', pleko: true, haddock: false, type: 'bool' },
  { feature: 'Importación masiva desde Excel', pleko: true, haddock: false, type: 'bool' },
  { feature: 'Precios de mercado (Mercabarna...)', pleko: true, haddock: false, type: 'bool' },
  { feature: 'Reportes de rentabilidad', pleko: true, haddock: false, type: 'bool' },
];

const exclusiveFeatures = [
  {
    icon: Euro,
    title: 'Ahorro real desde el primer mes',
    desc: 'Desde 49€/mes con todo incluido. Sin módulos adicionales que inflan la factura. Haddock empieza en 85€/mes con menos funcionalidades.',
  },
  {
    icon: Clock,
    title: 'Control horario integrado',
    desc: 'Cumple con el RD-ley 8/2019 sin contratar otro software. Fichajes, pausas e informes de inspección incluidos en PRO y SCALE.',
  },
  {
    icon: QrCode,
    title: 'Carta digital con QR y alérgenos',
    desc: 'Diseña tu carta digital, genera códigos QR y publica al instante. Vinculada a tus recetas para ver el coste real de cada plato y los alérgenos.',
  },
  {
    icon: Shield,
    title: 'IA de última generación',
    desc: 'Genera recetas con IA, optimiza tu food cost automáticamente y escanea facturas con OCR para actualizar precios al instante.',
  },
  {
    icon: Package,
    title: 'Almacén y control de stock',
    desc: 'Gestión de inventario con movimientos de entrada/salida, alertas de stock mínimo y conexión directa con el OCR de facturas.',
  },
  {
    icon: Scale,
    title: 'Despieces y escalador de raciones',
    desc: 'Calcula el coste real de piezas grandes (atún, ternera) con mermas de limpieza. Escala recetas a cualquier número de raciones para producción.',
  },
  {
    icon: FlaskConical,
    title: 'Laboratorio Chef (I+D)',
    desc: 'Espacio para experimentar con nuevas recetas. Registra iteraciones, puntuaciones de sabor/textura/presentación y fotos del proceso.',
  },
  {
    icon: Leaf,
    title: 'Calendario de temporada',
    desc: '86 productos de temporada en España con meses óptimos de compra. Aprovecha los mejores precios y la máxima calidad del producto.',
  },
];

const migrationSteps = [
  { step: '1', title: 'Exporta de Haddock', desc: 'Descarga tus ingredientes en Excel desde Haddock.' },
  { step: '2', title: 'Importa en PLEKO', desc: 'Usa nuestro asistente de importación Excel en 3 pasos.' },
  { step: '3', title: 'Configura recetas', desc: 'Crea tus escandallos con nuestro formulario asistido.' },
  { step: '4', title: 'Listo para operar', desc: 'Tu equipo puede empezar a trabajar en menos de un día.' },
];

export default function AlternativaHaddockPage() {
  return (
    <>
      <Helmet>
        <title>Alternativa a Haddock para Restaurantes 2026 | PLEKO vs Haddock</title>
        <meta
          name="description"
          content="Comparativa PLEKO vs Haddock 2026. PLEKO ofrece escandallo automático, control horario, almacén, carta digital QR, IA y OCR desde 49€/mes. 14 días gratis sin tarjeta."
        />
        <meta name="keywords" content="alternativa haddock, haddock alternativa, pleko vs haddock, software restaurante alternativa, alternativa a haddock, haddock app alternativa, comparativa haddock" />
        <link rel="canonical" href="https://pleko.app/alternativa-haddock" />
        <meta property="og:title" content="Alternativa a Haddock para Restaurantes 2026 | PLEKO" />
        <meta property="og:description" content="Comparativa PLEKO vs Haddock 2026. Más funcionalidades, más asequible. Escandallo, OCR, control horario, almacén, carta QR y IA desde 49€/mes." />
        <meta property="og:url" content="https://pleko.app/alternativa-haddock" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Alternativa a Haddock para Restaurantes 2026 | PLEKO" />
        <meta name="twitter:description" content="Comparativa PLEKO vs Haddock 2026. Más funcionalidades, más asequible. Desde 49€/mes." />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-blue-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                Comparativa actualizada 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                La <span className="text-blue-500">alternativa a Haddock</span> con más funcionalidades y menor precio
              </h1>
              <p className="text-lg sm:text-xl text-slate-600 mb-4 max-w-2xl mx-auto">
                PLEKO incluye todo lo que tiene Haddock y <strong>12 funcionalidades que Haddock no ofrece</strong>: control horario, almacén, carta digital con QR, IA generativa, despieces, laboratorio chef y más.
              </p>
              <p className="text-base text-slate-500 mb-8">
                El <strong className="text-blue-600">mejor precio del mercado: 49€/mes</strong> frente a los 85€/mes de Haddock. Sin costes ocultos ni módulos de pago.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors shadow-lg shadow-blue-500/25"
                >
                  Probar PLEKO gratis 14 días
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="#comparativa"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-colors border border-slate-200"
                >
                  Ver comparativa completa
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Quick stats bar */}
        <section className="border-y border-slate-100 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <p className="text-2xl font-bold text-blue-600">49€<span className="text-base font-normal text-slate-500">/mes</span></p>
                <p className="text-sm text-slate-500 mt-1">El mejor precio del mercado</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-red-400 line-through">85€<span className="text-base font-normal text-red-300">/mes</span></p>
                <p className="text-sm text-slate-500 mt-1">Precio Haddock</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">+12</p>
                <p className="text-sm text-slate-500 mt-1">Funciones exclusivas PLEKO</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">14 días</p>
                <p className="text-sm text-slate-500 mt-1">Prueba gratis sin tarjeta</p>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section id="comparativa" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-3 text-center">PLEKO vs Haddock: comparativa completa 2026</h2>
          <p className="text-slate-500 text-center mb-10 max-w-2xl mx-auto">
            Comparamos funcionalidad por funcionalidad. Las filas destacadas en azul son funcionalidades que solo PLEKO ofrece.
          </p>
          <div className="overflow-x-auto mb-12 rounded-xl border border-slate-200">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="p-4 text-left font-medium">Funcionalidad</th>
                  <th className="p-4 text-center font-medium">
                    <span className="text-blue-300">PLEKO</span>
                  </th>
                  <th className="p-4 text-center font-medium">Haddock</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map(({ feature, pleko, haddock, type, highlight }, i) => (
                  <tr
                    key={feature}
                    className={
                      highlight
                        ? 'bg-blue-50 border-l-4 border-l-blue-400'
                        : i % 2 === 0 ? 'bg-white' : 'bg-slate-50'
                    }
                  >
                    <td className={`p-4 border-b border-slate-100 font-medium ${highlight ? 'text-blue-900' : 'text-slate-800'}`}>
                      {feature}
                      {highlight && typeof haddock === 'boolean' && !haddock && (
                        <span className="ml-2 text-[10px] font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">EXCLUSIVO</span>
                      )}
                    </td>
                    <td className="p-4 border-b border-slate-100 text-center">
                      {type === 'bool' ? (
                        pleko ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-400 mx-auto" />
                        )
                      ) : (
                        <span className="font-semibold text-blue-600">{pleko as string}</span>
                      )}
                    </td>
                    <td className="p-4 border-b border-slate-100 text-center">
                      {type === 'bool' ? (
                        haddock ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-400 mx-auto" />
                        )
                      ) : (
                        <span className="text-slate-600">{haddock as string}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mid-page CTA */}
          <div className="text-center mb-16">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors"
            >
              Probar PLEKO gratis 14 días
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-sm text-slate-400 mt-2">Sin tarjeta de crédito · Todas las funciones PRO</p>
          </div>

          {/* Exclusive features grid */}
          <h2 className="text-3xl font-bold text-slate-900 mb-3 text-center">Lo que Haddock no tiene</h2>
          <p className="text-slate-500 text-center mb-10 max-w-2xl mx-auto">
            12 funcionalidades que solo encontrarás en PLEKO. Diseñadas para restaurantes que quieren ir más allá del escandallo básico.
          </p>
          <div className="grid md:grid-cols-2 gap-5 mb-16">
            {exclusiveFeatures.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="border border-slate-200 rounded-xl p-6 hover:border-blue-200 hover:bg-blue-50/30 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-lg mb-1">{title}</h3>
                    <p className="text-slate-600 leading-relaxed text-sm">{desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Migration steps */}
          <h2 className="text-3xl font-bold text-slate-900 mb-3 text-center">Migrar de Haddock a PLEKO en 4 pasos</h2>
          <p className="text-slate-500 text-center mb-10 max-w-2xl mx-auto">
            No pierdas tus datos. Migra de Haddock a PLEKO en menos de un día sin complicaciones.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
            {migrationSteps.map(({ step, title, desc }) => (
              <div key={step} className="text-center p-5 rounded-xl border border-slate-200 hover:border-blue-200 transition-colors">
                <div className="w-10 h-10 rounded-full bg-blue-500 text-white font-bold text-lg flex items-center justify-center mx-auto mb-3">
                  {step}
                </div>
                <h3 className="font-bold text-slate-900 text-sm mb-1">{title}</h3>
                <p className="text-slate-500 text-xs leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          {/* FAQ */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Preguntas frecuentes sobre la alternativa a Haddock</h2>
            <div className="space-y-3">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden group">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4 group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 sm:p-10 text-center text-white">
            <ChefHat className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Cambia a PLEKO y empieza a ahorrar hoy</h2>
            <p className="text-blue-100 mb-6 max-w-xl mx-auto">
              Más funcionalidades que Haddock, desde 49€/mes. Migra tus datos en minutos, configura tus recetas y empieza a controlar tus costes de verdad.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-blue-600 font-bold rounded-xl hover:bg-blue-50 transition-colors"
              >
                Empezar gratis 14 días
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/precios"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-blue-400/30 text-white font-semibold rounded-xl hover:bg-blue-400/50 transition-colors border border-white/20"
              >
                Ver precios
              </Link>
            </div>
            <p className="text-blue-200 text-sm mt-4">Sin tarjeta de crédito · Sin compromiso · Soporte incluido</p>
          </div>
        </section>
      </div>
    </>
  );
}
